import { useEffect, useRef, useState } from 'preact/hooks';
import mapboxgl from 'mapbox-gl';
import { render } from 'preact';
import MapMarkerHtml from './MapMarkerHtml';

const DEFAULT_ICON = 'map-icon-red';

/**
 * Derives a stable Mapbox image ID from a URL.
 * Uses the filename from the URL (part after the last "/").
 */
const getImageId = url => `map-icon-eurovia--${url.split('/').pop().replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-')}`;

/**
 * Loads an SVG (or any image) from a URL and registers it with the Mapbox map instance.
 * Resolves with the image ID on success, or DEFAULT_ICON as graceful fallback.
 */
const loadMapImage = (mapInstance, url) =>
  new Promise(resolve => {
    const imageId = getImageId(url);
    if (mapInstance.hasImage(imageId)) {
      resolve(imageId);
      return;
    }
    const img = new Image(40, 40);
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        if (!mapInstance.hasImage(imageId)) {
          mapInstance.addImage(imageId, img);
        }
        resolve(imageId);
      } catch (e) {
        console.warn(`Failed to add map icon "${url}":`, e);
        resolve(DEFAULT_ICON);
      }
    };
    img.onerror = () => {
      console.warn(`Failed to load map icon: ${url}`);
      resolve(DEFAULT_ICON);
    };
    img.src = url;
  });

const Map = ({filteredLocations, accessToken}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [quotaAllowed, setQuotaAllowed] = useState(null);
  const [quotaMessage, setQuotaMessage] = useState('');

  // init map
  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    fetch('/map-quota', {method: 'POST'})
      .then(res => res.json())
      .then(data => {
        if (!data.allowed) {
          setQuotaAllowed(false);
          setQuotaMessage(data.message || '');
          return;
        }
        setQuotaAllowed(true);
        initMap();
      })
      .catch(() => {
        setQuotaAllowed(true);
        initMap();
      });

    function initMap() {
      mapboxgl.accessToken = accessToken;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        // style: 'mapbox://styles/mapbox/streets-v12',
        style: 'mapbox://styles/eurovia-deutschland/cmpmqb76l000o01s7crb901gf',
        center: [10.4515, 51.1657], // center germany
        zoom: 5.5,
      });

      map.current.on('load', () => {
        // cluster settings
        map.current.addSource('locations', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
        });

        // cluster styling
        map.current.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'locations',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': ['step', ['get', 'point_count'], '#004488', 10, '#004488', 30, '#004488'],
            'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 30, 40],
          },
        });

        // cluster count
        map.current.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'locations',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
          },
          paint: {
            'text-color': '#fff',
          },
        });
        // single marker - icon resolved per feature via 'iconImageId' property
        map.current.addLayer({
          id: 'unclustered-point',
          type: 'symbol',
          source: 'locations',
          filter: ['!', ['has', 'point_count']],
          layout: {
            'icon-image': ['get', 'iconImageId'],
            'icon-size': 0.7,
            'icon-anchor': 'bottom',
            'icon-allow-overlap': true,
          },
        });

        // cluster click-event
        map.current.on('click', 'clusters', e => {
          const features = map.current.queryRenderedFeatures(e.point, {
            layers: ['clusters'],
          });
          const clusterId = features[0].properties.cluster_id;
          map.current.getSource('locations').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.current.easeTo({
              center: features[0].geometry.coordinates,
              zoom,
            });
          });
        });

        // marker click-event
        map.current.on('click', 'unclustered-point', e => {
          const coordinates = e.features[0].geometry.coordinates.slice();
          const location = e.features[0].properties;

          if (!location.uid) return;

          const div = document.createElement('div');
          render(<MapMarkerHtml location={location}/>, div);

          new mapboxgl.Popup().setLngLat(coordinates).setDOMContent(div).addTo(map.current);
        });
        // change cursor on hover
        map.current.on('mouseenter', 'clusters', () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'clusters', () => {
          map.current.getCanvas().style.cursor = '';
        });
        map.current.on('mouseenter', 'unclustered-point', () => {
          map.current.getCanvas().style.cursor = 'pointer';
        });
        map.current.on('mouseleave', 'unclustered-point', () => {
          map.current.getCanvas().style.cursor = '';
        });

        setMapLoaded(true);
      });
    } // end initMap

    return () => map.current?.remove();
  }, [accessToken]);

  // update marker on map
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const source = map.current.getSource('locations');
    if (!source) return;

    const validLocations = (filteredLocations || []).filter(location => location.longitude && location.latitude);
    // collect unique custom icon URLs to preload before updating source data
    const uniqueIconUrls = [...new Set(validLocations.map(loc => loc.mapIcon).filter(Boolean))];
    Promise.all(uniqueIconUrls.map(url => loadMapImage(map.current, url))).then(loadedIds => {
      // guard: map may have been removed while images were loading
      if (!map.current) return;
      const src = map.current.getSource('locations');
      if (!src) return;
      // build url -> resolved imageId lookup
      const iconIdMap = Object.fromEntries(uniqueIconUrls.map((url, i) => [url, loadedIds[i]]));
      const geojson = {
        type: 'FeatureCollection',
        features: validLocations.map(location => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(location.longitude), parseFloat(location.latitude)],
          },
          properties: {
            uid: location.uid,
            name: location.name,
            zip: location.zip || '',
            city: location.city || '',
            address: location.address || '',
            addressExtra: location.addressExtra || '',
            currentOpeningStatus: location.currentOpeningStatus || '',
            phone: location.phone || '',
            mail: location.mail || '',
            website: location.website || '',
            url: location.url || '',
            googleRouteUrl: location.googleRouteUrl || '',
            // use the loaded custom icon, or fall back to the default sprite icon
            iconImageId: location.mapIcon ? (iconIdMap[location.mapIcon] || DEFAULT_ICON) : DEFAULT_ICON,
          },
        })),
      };
      src.setData(geojson);
      // adapt map to filtered markers
      if (validLocations.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();

        validLocations.forEach(location => {
          bounds.extend([parseFloat(location.longitude), parseFloat(location.latitude)]);
        });

        map.current.fitBounds(bounds, {
          padding: {top: 50, bottom: 50, left: 50, right: 50},
          maxZoom: 15,
          duration: 1000,
        });
      }
    });
  }, [filteredLocations, mapLoaded]);

  if (quotaAllowed === false) {
    return (
      <div className="uberall-locations__map-quota-exceeded">
        {quotaMessage}
      </div>
    );
  }

  return <div ref={mapContainer} className="uberall-locations__map"/>;
};

export default Map;
